import React, { useState } from 'react';
import { FileText, Plus, Send } from 'lucide-react';
import Cookies from 'js-cookie';

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return '';
  const sec = Math.floor((Date.now() - then) / 1000);
  if (sec < 45) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`;
  const d = new Date(dateStr);
  const opts = { day: 'numeric', month: 'short' };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-IN', opts);
};

const LogCard = ({ data, onCommentSubmit, isSubmitting }) => {
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentText, setCommentText] = useState('');

  /** Reference-style: "5 May, 02:30 PM" (no ordinal on day). */
  const formatDateTimeCompact = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return '---';
    const day = date.getDate();
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const time = date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return `${day} ${month}, ${time}`;
  };

  // If title is "No Answer. Next follow up is on January 1, 1970", show only "No Answer"
  const getDisplayTitle = (raw) => {
    if (!raw) return 'Activity Title';
    const epochSuffix = '. Next follow up is on January 1, 1970';
    if (raw.endsWith(epochSuffix)) {
      return raw.slice(0, -epochSuffix.length).trim();
    }
    return raw;
  };

  /** Placeholder written by the saleslogs converter when a log arrives without a body. */
  const PLACEHOLDER_DESCRIPTION = 'no description provided';

  const normalizeLine = (s) =>
    String(s || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const rawDescRaw = (data?.description && String(data.description).trim()) || '';
  const rawDesc =
    normalizeLine(rawDescRaw) === PLACEHOLDER_DESCRIPTION ? '' : rawDescRaw;
  const rawName = (data?.name && String(data.name).trim()) || '';
  const titleFromDesc = rawDesc ? getDisplayTitle(rawDesc) : '';
  const titleFromName = rawName ? getDisplayTitle(rawName) : '';

  // Most logs are written with name === description (the whole narrative in both).
  // Some — prelead notes copied on qualify, qualification-profile edits — use name as
  // a short label and description as the actual note, so the note is what must be shown.
  const bodyText = titleFromDesc || titleFromName || 'Activity Title';
  const labelText =
    titleFromName && normalizeLine(titleFromName) !== normalizeLine(bodyText)
      ? titleFromName
      : '';

  /** One clock for the row (when the entry was logged). */
  const loggedAt = data?.createdAt || data?.updatedAt;
  const formattedDateTime = formatDateTimeCompact(loggedAt);

  const timelineIconClass =
    'bg-teal-700 text-white shadow-md ring-2 ring-white';

  /** Single narrative shown in the card (reference: one body block, no duplicate heading). */
  const cardMainText = bodyText;

  const handleCommentSubmit = async () => {
    if (!commentText.trim()) return;

    try {
      await onCommentSubmit(data.id, commentText);
      setCommentText('');
      // Stay in thread view; parent refetch populates comment. Avoid toggling
      // showCommentInput off here so the card does not flash closed before data arrives.
    } catch (error) {
      console.error('Error handling comment submission:', error);
    }
  };

  // Comments present (array or non-empty string)
  const hasComments = () => {
    if (Array.isArray(data.comment)) {
      return data.comment.length > 0;
    }
    return Boolean(data.comment);
  };

  const getInitials = (name) =>
    !name
      ? ''
      : name
          .split(' ')
          .map((n) => n[0])
          .join('')
          .toUpperCase();

  const hasMetaBelowBody = Boolean(data && data.createdBy);
  const showThreadCard = hasComments() || showCommentInput;
  const showCommentBtn = !showThreadCard;

  const bitsForMe = [Cookies.get('userRole'), Cookies.get('userDesignation')].filter(Boolean).join(' ');
  const currentUserInitials = getInitials(bitsForMe || 'You');

  const commentAuthorLine = (commentObj) => {
    const name = commentObj.user?.name || 'Unknown User';
    const r = commentObj.user?.designation?.name || commentObj.user?.role_name;
    const role =
      r ||
      (typeof commentObj.user?.role === 'string'
        ? commentObj.user.role
        : commentObj.user?.role?.name) ||
      '';
    return role ? `${name} (${role})` : name;
  };

  return (
    <div className="relative flex w-full min-w-0 gap-3 pb-10">
      {/* Timeline spine - full row height; ring masks line at dot */}
      <div className="relative flex w-11 flex-shrink-0 flex-col items-center self-stretch">
        <div className="absolute bottom-0 left-1/2 top-0 w-px -translate-x-1/2 bg-gray-200" aria-hidden />
        <div
          className={`relative z-[1] mt-7 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${timelineIconClass}`}>
          <FileText className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <time
          dateTime={loggedAt ? new Date(loggedAt).toISOString() : undefined}
          className="block text-xs font-medium tracking-wide text-gray-600">
          {formattedDateTime}
        </time>

        {showThreadCard ? (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
            {/* Main log body */}
            <div className="px-4 py-4">
              {labelText && (
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-teal-700">
                  {labelText}
                </p>
              )}
              <p className="whitespace-pre-line text-[15px] leading-relaxed text-gray-800">
                {cardMainText}
              </p>
              {hasMetaBelowBody && (
                <p className="mt-3 text-xs text-gray-500">
                  <span className="text-gray-400">Recorded by </span>
                  <span className="font-medium text-gray-700">{data.createdBy.name}</span>
                </p>
              )}
            </div>

            <div className="h-px bg-gray-200" aria-hidden />

            {/* Existing comments - bubble layout */}
            {Array.isArray(data.comment) && data.comment.length > 0 && (
              <div className="divide-y divide-gray-100 bg-white px-2 py-2">
                {data.comment.map((commentObj, idx) => {
                  const pic =
                    commentObj.user?.user_pic?.url || commentObj.user?.profile_pic?.url || commentObj.user?.avatar;
                  const when = formatRelativeTime(commentObj.createdAt || commentObj.updatedAt);
                  return (
                    <div key={idx} className="flex gap-3 px-2 py-3">
                      <div className="flex-shrink-0 pt-0.5">
                        {pic ? (
                          <img
                            src={pic}
                            alt=""
                            className="h-9 w-9 rounded-full object-cover ring-2 ring-white"
                          />
                        ) : (
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-600 text-xs font-semibold text-white ring-2 ring-white">
                            {getInitials(commentObj.user?.name)}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1 rounded-lg bg-gray-100 px-3 py-2.5">
                        <div className="mb-1 flex items-start justify-between gap-3">
                          <span className="text-sm font-medium text-teal-700">
                            {commentAuthorLine(commentObj)}
                          </span>
                          {when && (
                            <span className="shrink-0 text-xs font-normal text-gray-400">{when}</span>
                          )}
                        </div>
                        <p className="text-sm leading-relaxed text-gray-700">{commentObj.comment}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Compose - avatar + field + send */}
            <div className="border-t border-gray-200 bg-[#fafafa] px-4 py-3">
              <div className="flex gap-3">
                <div
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-teal-600 text-xs font-semibold text-white ring-2 ring-white"
                  aria-hidden>
                  {currentUserInitials}
                </div>
                <div className="relative min-w-0 flex-1">
                  <input
                    type="text"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Add a comment..."
                    disabled={isSubmitting}
                    className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-3 pr-12 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 disabled:opacity-60"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCommentSubmit();
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleCommentSubmit}
                    disabled={!commentText.trim() || isSubmitting}
                    className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-teal-700 transition-colors hover:bg-teal-50 disabled:opacity-40"
                    aria-label="Send comment">
                    <Send className="h-4 w-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
              {showCommentInput && !hasComments() && (
                <button
                  type="button"
                  onClick={() => {
                    setShowCommentInput(false);
                    setCommentText('');
                  }}
                  className="mt-2 text-xs font-medium text-gray-500 hover:text-gray-700">
                  Cancel
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200/90 bg-[#f8f9fa] px-4 py-4 shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
            {labelText && (
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-teal-700">
                {labelText}
              </p>
            )}
            <p className="whitespace-pre-line text-[15px] leading-relaxed text-gray-800">
              {cardMainText}
            </p>
            {hasMetaBelowBody && (
              <p className="mt-3 text-xs text-gray-500">
                <span className="text-gray-400">Recorded by </span>
                <span className="font-medium text-gray-700">{data.createdBy.name}</span>
              </p>
            )}
            {showCommentBtn && (
              <button
                type="button"
                onClick={() => setShowCommentInput(true)}
                className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-teal-700 transition-colors hover:text-teal-800">
                <span className="flex h-6 w-6 items-center justify-center rounded border border-teal-600/35 bg-white shadow-sm">
                  <Plus className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
                </span>
                Comment
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LogCard;
